/**
 * Calculates a weighted score for a practice based on selected priority rankings.
 *
 * @param {Object} practice - A transformed practice object with a `priorities` array.
 * @param {Array<{priority: string, score: number}>} rankingScore - Array of user-ranked priorities, where earlier entries are more important.
 * @returns {number} Weighted score based on ranking priority and multipliers.
 */
export const calculatePracticeScore = (practice, rankingScore = []) => {
  if (!practice || typeof practice !== "object") return 0;

  const multipliers = [1, 0.75, 0.5, 0.25]; // based on priority order
  let totalScore = 0;

  rankingScore.forEach((ranked, index) => {
    const priority = ranked.priority;
    const weight = multipliers[index] ?? 0.25;

    const match = practice.priorities?.find((p) => p.name === priority);
    const value = match?.rating;

    if (typeof value === "number" && !isNaN(value)) {
      totalScore += value * weight;
    }
  });

  return totalScore;
};

/**
 * Assigns a match label ("Best", "Good", "Fair") based on the calculated score and number of selected priorities.
 *
 * @param {number} score - The weighted score for the practice.
 * @param {number} totalPriorities - Number of priorities selected by the user (used to determine score band ranges).
 * @returns {{ scoreLabel: string, scoreClassName: string }} Object containing a human-readable label and CSS class for styling.
 */
export const getMatchLabel = (score, totalPriorities) => {
  const ranges = {
    0: [],
    1: [
      { min: 1, max: 1, label: "Fair Match", className: "practice-fair-match" },
      { min: 2, max: 2, label: "Good Match", className: "practice-good-match" },
      { min: 3, max: 3, label: "Best Match", className: "practice-best-match" },
    ],
    2: [
      {
        min: 0.75,
        max: 2.25,
        label: "Fair Match",
        className: "practice-fair-match",
      },
      {
        min: 2.26,
        max: 3.75,
        label: "Good Match",
        className: "practice-good-match",
      },
      {
        min: 3.76,
        max: 5.25,
        label: "Best Match",
        className: "practice-best-match",
      },
    ],
    3: [
      {
        min: 0.5,
        max: 2.5,
        label: "Fair Match",
        className: "practice-fair-match",
      },
      {
        min: 2.6,
        max: 5.0,
        label: "Good Match",
        className: "practice-good-match",
      },
      {
        min: 5.1,
        max: 6.75,
        label: "Best Match",
        className: "practice-best-match",
      },
    ],
    4: [
      {
        min: 0.25,
        max: 2.75,
        label: "Fair Match",
        className: "practice-fair-match",
      },
      {
        min: 2.76,
        max: 5.5,
        label: "Good Match",
        className: "practice-good-match",
      },
      {
        min: 5.6,
        max: 10,
        label: "Best Match",
        className: "practice-best-match",
      },
    ],
  };

  const key = totalPriorities >= 4 ? 4 : totalPriorities;
  const bands = ranges[key] || [];

  for (const range of bands) {
    if (score >= range.min && (range.max === undefined || score <= range.max)) {
      return { scoreLabel: range.label, scoreClassName: range.className };
    }
  }

  return { scoreLabel: "", scoreClassName: "" };
};
